import { useState } from 'react';
import { respondentCleanupApi, CleanupScanResponse, CleanupAffectedRow } from '@/services/api';
import { formatDateTime } from '@/utils/dateFormat';

const CONFIRM_PHRASE = 'FIX';

const statusBadgeColor = (status: string): string => {
  switch (status) {
    case 'completed':
      return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    case 'terminated':
      return 'bg-red-50 text-red-700 border border-red-200';
    case 'quota_full':
      return 'bg-amber-50 text-amber-700 border border-amber-200';
    case 'security_terminated':
      return 'bg-purple-50 text-purple-700 border border-purple-200';
    case 'started':
    case 'invited':
      return 'bg-blue-50 text-blue-700 border border-blue-200';
    default:
      return 'bg-[#F3F5F7] text-[#6B7280] border border-[#ECEEF1]';
  }
};

const downloadCsv = (filename: string, rows: CleanupAffectedRow[]) => {
  const header = [
    'id',
    'masked_id',
    'current_status',
    'block_type',
    'invited_at',
    'started_at',
    'completed_at',
    'project_supplier_id',
    'collection_id',
  ];
  const lines = rows.map((r) =>
    [
      r.id,
      r.maskedId,
      r.status,
      r.blockType,
      r.invitedAt ?? '',
      r.startedAt ?? '',
      r.completedAt ?? '',
      r.projectSupplierId,
      r.collectionId ?? '',
    ]
      .map((v) => `"${String(v).replace(/"/g, '""')}"`)
      .join(',')
  );
  const csv = [header.join(','), ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const RespondentCleanupPage = () => {
  const [scan, setScan] = useState<CleanupScanResponse | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applyResult, setApplyResult] = useState<{ updatedCount: number } | null>(null);

  const runScan = async () => {
    setScanning(true);
    setScanError(null);
    setApplyResult(null);
    try {
      const res = await respondentCleanupApi.scan();
      if (res.success && res.data) {
        setScan(res.data);
      } else {
        setScanError(res.error || 'Scan failed');
      }
    } catch (e: any) {
      setScanError(e?.response?.data?.error || e?.message || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const runApply = async () => {
    if (!scan) return;
    setApplying(true);
    setApplyError(null);
    try {
      const res = await respondentCleanupApi.apply(scan.total);
      if (res.success && res.data) {
        setApplyResult(res.data);
        setShowConfirm(false);
        setConfirmInput('');
        // Refresh scan to confirm zero remaining
        await runScan();
      } else {
        setApplyError(res.error || 'Apply failed');
      }
    } catch (e: any) {
      setApplyError(
        e?.response?.data?.error ||
          e?.message ||
          'Apply failed (the affected count may have changed — re-scan and try again)'
      );
    } finally {
      setApplying(false);
    }
  };

  const canApply = scan && scan.total > 0;
  const sampleRows = scan?.rows ?? [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="text-3xl font-bold font-heading text-[#1A1F2B] mb-1">Respondent Cleanup</h1>
        <p className="text-[#6B7280] text-[15px]">
          Backfill tool for respondents whose RD block verdict was overwritten by a later
          /complete, /terminate, or /quota hit. Affected rows have <code>rd_block_reason</code> set
          but a <code>status</code> other than <code>security_terminated</code>.
        </p>
      </div>

      {/* Action bar */}
      <div className="bg-white border border-[#ECEEF1] rounded-xl p-6 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#1A1F2B]">Step 1 — Scan</h2>
          <p className="text-sm text-[#6B7280] mt-1">
            Read-only. Counts how many respondents are affected and shows a sample.
          </p>
        </div>
        <button
          onClick={runScan}
          disabled={scanning}
          className="px-4 py-2 rounded-lg bg-[#18A5F9] text-white font-medium hover:bg-[#1493e0] disabled:opacity-50"
        >
          {scanning ? 'Scanning…' : scan ? 'Re-scan' : 'Run Scan'}
        </button>
      </div>

      {scanError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
          <strong>Scan failed:</strong> {scanError}
        </div>
      )}

      {/* Results */}
      {scan && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white border border-[#ECEEF1] rounded-xl p-6">
              <div className="text-sm text-[#6B7280] mb-1">Total affected</div>
              <div className="text-3xl font-bold text-[#1A1F2B]">{scan.total}</div>
              {scan.rowsReturned < scan.total && (
                <div className="text-xs text-[#6B7280] mt-2">
                  Showing first {scan.rowsReturned} (capped at {scan.rowLimit}). Apply will fix all{' '}
                  {scan.total}.
                </div>
              )}
            </div>

            <div className="bg-white border border-[#ECEEF1] rounded-xl p-6">
              <div className="text-sm text-[#6B7280] mb-2">By current (wrong) status</div>
              {Object.keys(scan.byStatus).length === 0 ? (
                <div className="text-sm text-[#9AA3AF]">—</div>
              ) : (
                <ul className="space-y-1 text-sm">
                  {Object.entries(scan.byStatus).map(([k, v]) => (
                    <li key={k} className="flex justify-between">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs ${statusBadgeColor(k)}`}
                      >
                        {k}
                      </span>
                      <span className="font-mono text-[#1A1F2B]">{v}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bg-white border border-[#ECEEF1] rounded-xl p-6">
              <div className="text-sm text-[#6B7280] mb-2">By original RD block reason</div>
              {Object.keys(scan.byBlockType).length === 0 ? (
                <div className="text-sm text-[#9AA3AF]">—</div>
              ) : (
                <ul className="space-y-1 text-sm">
                  {Object.entries(scan.byBlockType).map(([k, v]) => (
                    <li key={k} className="flex justify-between">
                      <span className="text-[#1A1F2B]">{k}</span>
                      <span className="font-mono text-[#1A1F2B]">{v}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {scan.total > 0 && (
            <div className="bg-white border border-[#ECEEF1] rounded-xl">
              <div className="flex items-center justify-between p-4 border-b border-[#ECEEF1]">
                <h3 className="font-semibold text-[#1A1F2B]">
                  Affected respondents ({scan.rowsReturned} shown)
                </h3>
                <button
                  onClick={() =>
                    downloadCsv(
                      `affected-respondents-${new Date().toISOString().slice(0, 10)}.csv`,
                      sampleRows
                    )
                  }
                  className="text-sm px-3 py-1.5 rounded-lg border border-[#ECEEF1] hover:bg-[#F8FAFC] text-[#1A1F2B]"
                >
                  Download CSV
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8FAFC] text-[#6B7280] text-left">
                    <tr>
                      <th className="px-4 py-2 font-medium">Masked ID</th>
                      <th className="px-4 py-2 font-medium">Current Status</th>
                      <th className="px-4 py-2 font-medium">Block Type</th>
                      <th className="px-4 py-2 font-medium">Invited</th>
                      <th className="px-4 py-2 font-medium">Completed (wrong)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sampleRows.map((r) => (
                      <tr key={r.id} className="border-t border-[#ECEEF1]">
                        <td className="px-4 py-2 font-mono text-xs">{r.maskedId}</td>
                        <td className="px-4 py-2">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded-full text-xs ${statusBadgeColor(
                              r.status
                            )}`}
                          >
                            {r.status}
                          </span>
                        </td>
                        <td className="px-4 py-2">{r.blockType}</td>
                        <td className="px-4 py-2 text-[#6B7280]">{formatDateTime(r.invitedAt)}</td>
                        <td className="px-4 py-2 text-[#6B7280]">{formatDateTime(r.completedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Apply */}
          <div className="bg-white border border-[#ECEEF1] rounded-xl p-6 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#1A1F2B]">Step 2 — Apply Fix</h2>
              <p className="text-sm text-[#6B7280] mt-1">
                Sets <code>status = security_terminated</code>, clears <code>completedAt</code> and{' '}
                <code>loiSeconds</code>, preserves <code>startedAt</code> and{' '}
                <code>rdBlockReason</code>. {scan.total === 0 && 'Nothing to apply.'}
              </p>
            </div>
            <button
              onClick={() => {
                setApplyError(null);
                setShowConfirm(true);
              }}
              disabled={!canApply}
              className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Apply Fix to {scan.total} Respondents
            </button>
          </div>
        </>
      )}

      {applyResult && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-4">
          ✅ Updated {applyResult.updatedCount} respondent(s). Any supplier credit already issued
          for these "completes" needs separate reconciliation.
        </div>
      )}

      {applyError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">
          <strong>Apply failed:</strong> {applyError}
        </div>
      )}

      {/* Confirmation modal */}
      {showConfirm && scan && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-[#1A1F2B] mb-2">Confirm Apply</h3>
            <p className="text-sm text-[#6B7280] mb-4">
              You are about to update <strong>{scan.total}</strong> respondent(s). This is
              destructive and not automatically reversible. Type{' '}
              <code className="bg-[#F3F5F7] px-1.5 py-0.5 rounded">{CONFIRM_PHRASE}</code> to
              confirm.
            </p>
            <input
              type="text"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder={`Type ${CONFIRM_PHRASE} to confirm`}
              className="w-full border border-[#ECEEF1] rounded-lg px-3 py-2 mb-4 focus:outline-none focus:border-[#18A5F9]"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowConfirm(false);
                  setConfirmInput('');
                }}
                disabled={applying}
                className="px-4 py-2 rounded-lg border border-[#ECEEF1] text-[#1A1F2B] hover:bg-[#F8FAFC]"
              >
                Cancel
              </button>
              <button
                onClick={runApply}
                disabled={applying || confirmInput !== CONFIRM_PHRASE}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {applying ? 'Applying…' : `Apply Fix to ${scan.total}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RespondentCleanupPage;
